import { Injectable } from '@angular/core';

import { WarehouseLocation } from '../models/warehouse-location.model';

/**
 * What a typed location code means, in one place.
 *
 * The operation form and the revaluation screen both let an operator type a shelf code instead of
 * finding it in a list, and they must agree about what a code is. Two copies of this would be two
 * answers to "which shelf is A1", and the one that drifts would put stock somewhere the other screen
 * says it is not - the same argument `normaliseBatchNumber` makes on the backend for batch numbers,
 * and `weightCountedSql` makes for which rows a weight total covers.
 *
 * This resolves; it does not validate in the sense that matters. The backend still refuses a
 * `warehouseLocationId` that does not exist, and that refusal is what actually protects the ledger -
 * verified by posting a bogus id, which is answered `400 warehouseLocationId 9999 does not exist`.
 * Everything here is a convenience in front of that check, never a replacement for it.
 */
@Injectable({
  providedIn: 'root'
})
export class WarehouseLocationCodeService {

  /**
   * The id of the one location a typed code names, or -1.
   *
   * Matched against the whole `aisle` + `shelf` label rather than split into the two, and that is the
   * decision the rest of this rests on. Both columns are free-form `varchar(255)`, so `AA10` could be
   * aisle AA shelf 10 or aisle A shelf A10, and nothing in the schema decides which. Comparing the
   * joined label needs no such rule and so cannot be wrong about it.
   *
   * An ambiguous code returns -1 rather than the first match. Two locations that normalise to one
   * code is a naming problem in the warehouse, and quietly picking one of them would put stock on the
   * wrong shelf and then report it as though it were certain. Refused, not guessed - the same
   * standing the backend gives a batch number it cannot make sense of.
   */
  resolve(text: string, locations: WarehouseLocation[]): number {
    const matches = this.matches(text, locations);

    return matches.length === 1 ? matches[0].id : -1;
  }

  /**
   * Why a code did not resolve, or '' when it did or when nothing has been typed.
   *
   * The two failures are named apart because they are different problems with different fixes: a
   * code matching nothing is usually the wrong warehouse selected above, and a code matching more
   * than one location is two shelves labelled the same way.
   */
  error(text: string, locations: WarehouseLocation[]): string {
    if (this.normalise(text) === '') {
      return '';
    }

    const matches = this.matches(text, locations);

    if (matches.length === 1) {
      return '';
    }

    return matches.length > 1
      ? 'More than one location is written this way. Type it as it appears on the shelf label.'
      : 'No such location in the selected warehouse.';
  }

  /** The label a resolved id reads as, in the `aisle / shelf` form the lines and reports use. */
  label(locationId: number, locations: WarehouseLocation[]): string {
    const location = locations.filter((l) => l.id === locationId)[0];

    return location ? location.aisle + ' / ' + location.shelf : '';
  }

  /**
   * `A1`, `A/1`, `a-1` and `A 1` are the same shelf. A code is written down all four of those ways,
   * and refusing three of them would be refusing the operator's own handwriting. Case and separators
   * are the only things normalised away - nothing is inferred, reordered or padded.
   */
  private normalise(text: string): string {
    return (text || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  private matches(text: string, locations: WarehouseLocation[]): WarehouseLocation[] {
    const code = this.normalise(text);

    if (code === '') {
      return [];
    }

    return (locations || []).filter(
      (location) => this.normalise(location.aisle + location.shelf) === code);
  }
}
